import { useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  Box,
  Card,
  Chip,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import { RT } from '../../retention/theme/retentionTheme';
import { SoftChip } from '../../retention/components/ui';
import {
  AttendanceRing,
  BANDS,
  BandLegend,
  DataCheckNote,
  DISPLAY_FONT,
  EmptyBlock,
  GRADIENTS,
  GlassBar,
  GradientStatCard,
  HERO,
  LoadingBlock,
  MiniStat,
  Panel,
  PctPill,
  PercentBar,
  RecentDots,
  bandColor,
  fmtDate,
  fmtMonth,
  fmtNum,
  fmtPct,
  fmtShortDate,
  fmtTime,
  numberSx,
} from './components/ui';
import { BandDonut, TrendChart } from './components/charts';

const Sparkline = ({ points = [] }) => {
  const data = points.filter((point) => point.attendance !== null).map((point) => point.attendance);
  if (data.length < 2) return <Box sx={{ height: 40 }} />;
  const color = bandColor(data[data.length - 1]);
  return (
    <ReactApexChart
      type="area"
      height={40}
      series={[{ name: 'Attendance %', data }]}
      options={{
        chart: { sparkline: { enabled: true }, animations: { enabled: false } },
        stroke: { width: 2, curve: 'smooth' },
        colors: [color],
        fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.02 } },
        yaxis: { min: 0, max: 100 },
        tooltip: { fixed: { enabled: false }, x: { show: false }, y: { formatter: (value) => fmtPct(value) }, marker: { show: false } },
      }}
    />
  );
};

function SubjectCard({ subject }) {
  const color = bandColor(subject.attendance);
  return (
    <Card sx={{ borderRadius: 4, position: 'relative', overflow: 'hidden', transition: 'transform .2s, box-shadow .2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: RT.shadowHover } }}>
      <Box sx={{ height: 5, background: `linear-gradient(90deg, ${alpha(color, 0.5)}, ${color})` }} />
      <Stack gap={1.5} sx={{ p: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="center">
          <AttendanceRing value={subject.attendance} size={68} label="attend." />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 17, color: RT.ink }} noWrap title={subject.subjectName}>
              {subject.subjectName || '—'}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: subject.tutorName ? RT.ink2 : RT.faint }}>
              <Icon icon="mdi:account-tie" width={15} />
              <Typography variant="caption" sx={{ fontWeight: subject.tutorName ? 700 : 500 }} noWrap>
                {subject.tutorName || 'Tutor not assigned'}
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: RT.muted }} component="div">
              {subject.students} students
            </Typography>
          </Box>
        </Stack>
        <Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: RT.muted, fontWeight: 600 }}>
              Sessions done
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 800, color: RT.ink, ...numberSx }}>
              {subject.completedSessions} / {subject.totalSessions} · {fmtPct(subject.completion)}
            </Typography>
          </Stack>
          <PercentBar value={subject.completion} color="#6366F1" height={7} />
        </Box>
        <Sparkline points={subject.trend} />
        <Stack direction="row" justifyContent="space-between" gap={1}>
          <MiniStat label="Present" value={fmtNum(subject.present)} color={RT.success} />
          <MiniStat label="Absent" value={fmtNum(subject.absent)} color={RT.error} />
          <MiniStat label="Pending" value={fmtNum(subject.pendingSessions)} color={RT.warning} align="right" />
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ pt: 1, borderTop: `1px dashed ${RT.line}` }}>
          <Typography variant="caption" sx={{ color: RT.muted }}>
            Last: <b style={{ color: RT.ink2 }}>{fmtShortDate(subject.lastSessionDate)}</b>
          </Typography>
          <Typography variant="caption" sx={{ color: RT.muted }}>
            Next: <b style={{ color: RT.ink2 }}>{fmtShortDate(subject.nextSessionDate)}</b>
          </Typography>
          {subject.unmarkedSessions > 0 && (
            <Typography variant="caption" sx={{ color: RT.warning, fontWeight: 700 }}>
              {subject.unmarkedSessions} unmarked
            </Typography>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

const STUDENT_SORTS = {
  attendance: (row) => row.attendance ?? -1,
  studentName: (row) => (row.studentName || '').toLowerCase(),
  present: (row) => row.present,
  absent: (row) => row.absent,
  notMarked: (row) => row.notMarked,
  currentAbsentStreak: (row) => row.currentAbsentStreak,
};

function StudentMatrix({ students, subjects, onOpen }) {
  const [search, setSearch] = useState('');
  const [band, setBand] = useState('all');
  const [showMoved, setShowMoved] = useState(false);
  const [sort, setSort] = useState({ key: 'attendance', order: 'asc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const rows = useMemo(() => {
    const text = search.trim().toLowerCase();
    const getter = STUDENT_SORTS[sort.key];
    return students
      .filter((row) => showMoved || row.inBatch)
      .filter((row) => band === 'all' || row.band === band)
      .filter((row) => !text || `${row.studentName} ${row.admnNo}`.toLowerCase().includes(text))
      .sort((a, b) => {
        const left = getter(a);
        const right = getter(b);
        const result = left > right ? 1 : left < right ? -1 : 0;
        return sort.order === 'asc' ? result : -result;
      });
  }, [students, search, band, showMoved, sort]);

  const counts = useMemo(() => {
    const base = students.filter((row) => showMoved || row.inBatch);
    return BANDS.reduce((acc, item) => ({ ...acc, [item.key]: base.filter((row) => row.band === item.key).length }), { all: base.length });
  }, [students, showMoved]);

  const visible = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const sortLabel = (key, label) => (
    <TableSortLabel
      active={sort.key === key}
      direction={sort.key === key ? sort.order : 'desc'}
      onClick={() => setSort((prev) => ({ key, order: prev.key === key && prev.order === 'desc' ? 'asc' : 'desc' }))}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Stack gap={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search student or admission no…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          sx={{ width: { xs: '100%', md: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="solar:magnifer-linear" width={18} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" gap={0.75} flexWrap="wrap">
          {[{ key: 'all', label: 'All', color: RT.primary }, ...BANDS].map((item) => (
            <Chip
              key={item.key}
              size="small"
              label={`${item.label} · ${counts[item.key] || 0}`}
              onClick={() => {
                setBand(item.key);
                setPage(0);
              }}
              sx={{
                fontWeight: 700,
                bgcolor: band === item.key ? item.color : alpha(item.color, 0.1),
                color: band === item.key ? '#fff' : item.color,
                '&:hover': { bgcolor: band === item.key ? item.color : alpha(item.color, 0.2) },
              }}
            />
          ))}
        </Stack>
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Switch size="small" checked={showMoved} onChange={(event) => setShowMoved(event.target.checked)} />}
          label={<Typography variant="caption" sx={{ fontWeight: 600 }}>Show students who left the batch</Typography>}
        />
      </Stack>
      <TableContainer sx={{ border: `1px solid ${RT.line}`, borderRadius: 3 }}>
        <Table size="small" sx={{ minWidth: 900 + subjects.length * 110 }}>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#F8F9FC', fontWeight: 700, color: RT.ink2, fontSize: 12.5, py: 1.25, whiteSpace: 'nowrap' } }}>
              <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: '#F8F9FC !important' }}>{sortLabel('studentName', 'Student')}</TableCell>
              <TableCell>{sortLabel('attendance', 'Overall')}</TableCell>
              <TableCell align="right">{sortLabel('present', 'Attended')}</TableCell>
              <TableCell align="right">{sortLabel('absent', 'Absent')}</TableCell>
              <TableCell align="right">
                <Tooltip title="Sessions of the student's subjects (while in the batch) with no mark saved">{sortLabel('notMarked', 'Not marked')}</Tooltip>
              </TableCell>
              {subjects.map((subject) => (
                <TableCell key={subject.subjectEnrollmentId} align="center">
                  {subject.subjectName}
                </TableCell>
              ))}
              <TableCell>Last sessions</TableCell>
              <TableCell align="right">
                <Tooltip title="Absent in a row, counting back from the latest marked session">{sortLabel('currentAbsentStreak', 'Absent streak')}</Tooltip>
              </TableCell>
              <TableCell align="right">Last present</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row) => {
              const bySubject = new Map(row.subjects.map((subject) => [subject.subjectEnrollmentId, subject]));
              return (
                <TableRow key={row.batchAssignmentId} hover onClick={() => onOpen(row)} sx={{ cursor: 'pointer', '& td': { borderColor: RT.line, py: 1 }, opacity: row.inBatch ? 1 : 0.6 }}>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: '#fff', maxWidth: 230 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: RT.ink }} noWrap title={row.studentName}>
                      {row.studentName || '—'}
                    </Typography>
                    <Stack direction="row" gap={0.75} alignItems="center">
                      <Typography variant="caption" sx={{ color: RT.muted }}>
                        Adm {row.admnNo}
                      </Typography>
                      {!row.inBatch && <SoftChip label="Left batch" color={RT.faint} sx={{ height: 18, fontSize: 10.5 }} />}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <PercentBar value={row.attendance} height={7} />
                      <PctPill value={row.attendance} size="small" />
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ ...numberSx, fontWeight: 700 }}>
                    {row.present}/{row.marked}
                  </TableCell>
                  <TableCell align="right" sx={{ ...numberSx, color: row.absent ? RT.error : RT.faint, fontWeight: 700 }}>
                    {row.absent}
                  </TableCell>
                  <TableCell align="right" sx={{ ...numberSx, color: row.notMarked ? RT.warning : RT.faint, fontWeight: 700 }}>
                    {row.notMarked}
                  </TableCell>
                  {subjects.map((subject) => {
                    const cell = bySubject.get(subject.subjectEnrollmentId);
                    return (
                      <TableCell key={subject.subjectEnrollmentId} align="center">
                        {cell ? (
                          <Tooltip
                            title={`${cell.present} present · ${cell.absent} absent${cell.na ? ` · ${cell.na} N/A` : ''}${cell.notMarked ? ` · ${cell.notMarked} not marked` : ''} · ${cell.eligible} sessions`}
                            arrow
                          >
                            <Box
                              sx={{
                                display: 'inline-block',
                                minWidth: 64,
                                px: 1,
                                py: 0.4,
                                borderRadius: 2,
                                fontWeight: 800,
                                fontSize: 12,
                                color: cell.attendance === null ? RT.faint : '#fff',
                                bgcolor: cell.attendance === null ? '#F1F3F8' : bandColor(cell.attendance),
                                ...numberSx,
                              }}
                            >
                              {fmtPct(cell.attendance)}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" sx={{ color: RT.faint }}>
                            not enrolled
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <RecentDots recent={row.recent} />
                  </TableCell>
                  <TableCell align="right">
                    {row.currentAbsentStreak >= 2 ? (
                      <SoftChip icon="solar:fire-bold" label={`${row.currentAbsentStreak} in a row`} color={RT.error} sx={{ fontWeight: 800 }} />
                    ) : (
                      <Typography variant="caption" sx={{ color: RT.faint, ...numberSx }}>
                        {row.currentAbsentStreak}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ ...numberSx, color: RT.muted, fontSize: 12.5 }}>
                    {fmtDate(row.lastPresentDate)}
                  </TableCell>
                </TableRow>
              );
            })}
            {!visible.length && (
              <TableRow>
                <TableCell colSpan={9 + subjects.length}>
                  <EmptyBlock title="No students match" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Stack>
  );
}

function SessionLog({ sessions }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rows = useMemo(() => [...sessions].reverse(), [sessions]);
  if (!rows.length) return <EmptyBlock title="No completed sessions in this range" />;
  return (
    <>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, color: RT.muted, fontSize: 12, borderColor: RT.line, whiteSpace: 'nowrap' } }}>
              <TableCell>Date</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Tutor</TableCell>
              <TableCell>Lesson</TableCell>
              <TableCell align="right">Present</TableCell>
              <TableCell align="right">Absent</TableCell>
              <TableCell align="right">N/A</TableCell>
              <TableCell align="right">Not marked</TableCell>
              <TableCell>Attendance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
              <TableRow key={row.sessionId} sx={{ '& td': { borderColor: RT.line } }}>
                <TableCell sx={{ whiteSpace: 'nowrap', ...numberSx }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {fmtDate(row.sessionDate)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: RT.muted }}>
                    {fmtTime(row.fromTime)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {row.subjectName}
                  {row.wise && <SoftChip label="Wise" color="#0EA5E9" sx={{ ml: 0.75, height: 18, fontSize: 10.5 }} />}
                </TableCell>
                <TableCell sx={{ color: RT.ink2 }}>{row.tutorName || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Typography variant="caption" sx={{ color: RT.muted }} noWrap component="div" title={row.lessons || ''}>
                    {row.lessons || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ ...numberSx, color: RT.success, fontWeight: 700 }}>
                  {row.present}
                </TableCell>
                <TableCell align="right" sx={{ ...numberSx, color: row.absent ? RT.error : RT.faint, fontWeight: 700 }}>
                  {row.absent}
                </TableCell>
                <TableCell align="right" sx={{ ...numberSx, color: RT.faint, fontWeight: 700 }}>
                  {row.na}
                </TableCell>
                <TableCell align="right" sx={{ ...numberSx, color: row.notMarked ? RT.warning : RT.faint, fontWeight: 700 }}>
                  {row.notMarked}
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  {!row.unmarked ? (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <PercentBar value={row.attendance} height={6} />
                      <PctPill value={row.attendance} size="small" />
                    </Stack>
                  ) : (
                    <SoftChip label="Not marked" color={RT.warning} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </>
  );
}

const STATUS_COLORS = { Active: '#4ADE80', 'Temporary Inactive': '#FCD34D', Upcoming: '#7DD3FC', Completed: '#E9D5FF' };

export default function BatchView({ data, loading, onOpenStudent }) {
  const [trendMode, setTrendMode] = useState('session');
  const firstLoad = loading && !data;
  const batch = data?.batch;
  const summary = data?.summary;
  const subjects = data?.subjects || [];

  const trendPoints = useMemo(() => {
    if (trendMode === 'month') {
      return (data?.monthly || []).map((month) => ({ label: fmtMonth(month.month), sessions: month.sessions, attendance: month.attendance }));
    }
    return (data?.sessions || []).slice(-40).map((session) => ({
      label: `${fmtShortDate(session.sessionDate)} · ${session.subjectName || ''}`,
      sessions: session.marked,
      attendance: session.attendance,
    }));
  }, [data, trendMode]);

  if (firstLoad) {
    return (
      <Stack gap={2.5}>
        <LoadingBlock rows={3} />
        <LoadingBlock rows={8} />
      </Stack>
    );
  }
  if (!data) return <Panel><EmptyBlock title="Couldn't load this batch" /></Panel>;

  const cards = [
    {
      gradient: GRADIENTS.indigo,
      icon: 'solar:chart-square-bold-duotone',
      label: 'Batch attendance',
      value: fmtPct(summary.attendance),
      sub: `${fmtNum(summary.present)} present of ${fmtNum(summary.marked)} marks${summary.movedOut ? ' · incl. students who left' : ''}`,
      footer: <GlassBar value={summary.attendance} />,
    },
    {
      gradient: GRADIENTS.emerald,
      icon: 'solar:calendar-mark-bold-duotone',
      label: 'Sessions completed',
      value: `${summary.completedSessions} / ${summary.totalSessions}`,
      sub: `${fmtPct(summary.completion)} done · ${summary.pendingSessions} pending`,
      footer: <GlassBar value={summary.completion} />,
    },
    {
      gradient: GRADIENTS.violet,
      icon: 'solar:user-id-bold-duotone',
      label: 'Avg attendance per student',
      value: fmtPct(summary.avgStudentAttendance),
      sub: `${summary.students} students${summary.movedOut ? ` · ${summary.movedOut} left` : ''}`,
    },
    {
      gradient: GRADIENTS.rose,
      icon: 'solar:danger-triangle-bold-duotone',
      label: `Below ${summary.lowThreshold}%`,
      value: summary.lowStudents,
      sub: `${summary.perfectStudents} with 100% · ${summary.unmarkedSessions} unmarked sessions`,
    },
  ];

  return (
    <Stack gap={2.5}>
      <Card sx={{ borderRadius: 4, overflow: 'hidden', border: 'none', background: HERO, color: '#fff', position: 'relative' }}>
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', right: -60, top: -110, bgcolor: 'rgba(255,255,255,.08)' }} />
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2.5} alignItems={{ md: 'center' }} sx={{ p: { xs: 2.5, md: 3 }, position: 'relative' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.75 }}>
              <Chip size="small" label={batch.status} sx={{ bgcolor: STATUS_COLORS[batch.status] || '#E2E8F0', color: '#1E1B4B', fontWeight: 800 }} />
              <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600 }}>
                #{batch.batchId} {batch.serialNo ? `· ${batch.serialNo}` : ''}
              </Typography>
            </Stack>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: { xs: 24, md: 30 }, fontWeight: 700, lineHeight: 1.15 }}>
              {batch.batchName}
            </Typography>
            <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 1.25, opacity: 0.92 }}>
              {[
                ['solar:book-bookmark-bold-duotone', batch.courseName],
                ['solar:square-academic-cap-bold-duotone', [batch.className, batch.syllabusName].filter(Boolean).join(' · ')],
                ['solar:user-rounded-bold-duotone', batch.croName ? `CRO: ${batch.croName}` : 'CRO not assigned'],
                ['solar:calendar-bold-duotone', batch.startDate ? `Started ${fmtDate(batch.startDate)}` : null],
                ['solar:clock-circle-bold-duotone', batch.startTime ? `${fmtTime(batch.startTime)} – ${fmtTime(batch.endTime)}` : null],
              ]
                .filter(([, text]) => text)
                .map(([icon, text]) => (
                  <Stack key={icon} direction="row" gap={0.6} alignItems="center">
                    <Icon icon={icon} width={17} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {text}
                    </Typography>
                  </Stack>
                ))}
            </Stack>
          </Box>
          <Stack direction="row" gap={3} alignItems="center" sx={{ bgcolor: 'rgba(255,255,255,.1)', borderRadius: 4, px: 2.5, py: 1.75, backdropFilter: 'blur(6px)' }}>
            {[
              ['Students', summary.students],
              ['Subjects', summary.subjects],
              ['Absences', fmtNum(summary.absent)],
            ].map(([label, value]) => (
              <Box key={label} sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: 26, fontWeight: 700, lineHeight: 1.1, ...numberSx }}>{value}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600 }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Card>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
        {cards.map((card) => (
          <GradientStatCard key={card.label} {...card} />
        ))}
      </Box>

      <Panel icon="solar:book-2-bold-duotone" color="#0EA5E9" title="Subject wise" subtitle="Sessions done, attendance and trend of the last 12 sessions per subject">
        {subjects.length ? (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' } }}>
            {subjects.map((subject) => (
              <SubjectCard key={subject.subjectEnrollmentId} subject={subject} />
            ))}
          </Box>
        ) : (
          <EmptyBlock title="No subjects in this batch" />
        )}
      </Panel>

      <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' } }}>
        <Panel
          icon="solar:graph-new-up-bold-duotone"
          title="Attendance trend"
          subtitle={trendMode === 'session' ? 'Last 40 sessions: students marked and attendance %' : 'Per month'}
          action={
            <ToggleButtonGroup size="small" exclusive value={trendMode} onChange={(_, value) => value && setTrendMode(value)}>
              <ToggleButton value="session">Per session</ToggleButton>
              <ToggleButton value="month">Per month</ToggleButton>
            </ToggleButtonGroup>
          }
        >
          {trendPoints.length ? (
            <TrendChart points={trendPoints} sessionsLabel={trendMode === 'session' ? 'Students marked' : 'Sessions completed'} />
          ) : (
            <EmptyBlock title="No completed sessions in this range" />
          )}
        </Panel>
        <Panel icon="solar:pie-chart-2-bold-duotone" color="#7C3AED" title="Students by attendance" subtitle="Current students">
          <BandDonut bands={data.bands} />
          {summary.bestSession && (
            <Stack gap={0.5} sx={{ mt: 1, pt: 1.5, borderTop: `1px dashed ${RT.line}` }}>
              <Typography variant="caption" sx={{ color: RT.muted }}>
                Best session: <b style={{ color: RT.success }}>{fmtPct(summary.bestSession.attendance)}</b> · {fmtDate(summary.bestSession.sessionDate)} · {summary.bestSession.subjectName}
              </Typography>
              <Typography variant="caption" sx={{ color: RT.muted }}>
                Lowest session: <b style={{ color: RT.error }}>{fmtPct(summary.worstSession.attendance)}</b> · {fmtDate(summary.worstSession.sessionDate)} · {summary.worstSession.subjectName}
              </Typography>
            </Stack>
          )}
        </Panel>
      </Box>

      <Panel
        icon="solar:users-group-rounded-bold-duotone"
        title="Student wise attendance"
        subtitle="Overall and per subject. Click a student for the full view"
        action={<BandLegend />}
      >
        <StudentMatrix students={data.students || []} subjects={subjects} onOpen={onOpenStudent} />
      </Panel>

      <Panel icon="solar:checklist-minimalistic-bold-duotone" color={RT.success} title="Session log" subtitle="Every completed session, latest first">
        <SessionLog sessions={data.sessions || []} />
      </Panel>
      <DataCheckNote check={data.check} />
    </Stack>
  );
}
